import { BrowserProvider } from "ethers";

export const AMOY_CHAIN_ID = 80002;
export const AMOY_NETWORK = {
  chainId: "0x13882",
  chainName: "Polygon Amoy Testnet",
  nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
  rpcUrls: ["https://polygon-amoy.drpc.org"],
  blockExplorerUrls: ["https://amoy.polygonscan.com/"],
};

let announcedMetaMask = null;
let selectedWallet = null;
let discoveryStarted = false;
const waiting = new Set();

// Keep listening for delayed extension injection for the lifetime of the page.
export function startWalletDiscovery() {
  if (discoveryStarted) return;
  discoveryStarted = true;
  window.addEventListener("eip6963:announceProvider", (event) => {
    const { info, provider } = event.detail || {};
    if (info?.rdns !== "io.metamask" || typeof provider?.request !== "function")
      return;
    announcedMetaMask = { provider, info };
    waiting.forEach((notify) => notify());
  });
  window.dispatchEvent(new Event("eip6963:requestProvider"));
}

export async function getMetaMaskWallet() {
  startWalletDiscovery();
  if (selectedWallet) return selectedWallet;
  if (!announcedMetaMask) {
    await new Promise((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        waiting.delete(finish);
        resolve();
      };
      const timer = setTimeout(finish, 1000);
      waiting.add(finish);
      window.dispatchEvent(new Event("eip6963:requestProvider"));
    });
  }
  if (announcedMetaMask) {
    selectedWallet = announcedMetaMask;
  } else {
    const legacy = [
      ...(window.ethereum?.providers || []),
      window.ethereum,
    ].find(
      (provider) =>
        provider?.isMetaMask &&
        !provider.isUniswapWallet &&
        typeof provider.request === "function",
    );
    if (legacy)
      selectedWallet = {
        provider: legacy,
        info: { name: "MetaMask (legacy)" },
      };
  }
  if (!selectedWallet) {
    throw new Error(
      "MetaMask non è disponibile. Abilita l’estensione per questo sito e riprova.",
    );
  }
  return selectedWallet;
}

export function rpcErrorCode(error) {
  const nested =
    error?.data?.originalError ||
    error?.info?.error ||
    error?.error ||
    error?.cause;
  return nested && nested !== error
    ? (rpcErrorCode(nested) ?? error?.code)
    : error?.code;
}

export function walletErrorMessage(error) {
  const code = Number(rpcErrorCode(error));
  if (code === 4001) return "Richiesta annullata in MetaMask. Puoi riprovare.";
  if (code === -32002)
    return "C’è già una richiesta aperta: completala in MetaMask.";
  if (code === 4100) return "Autorizza questo sito in MetaMask e riprova.";
  if (code === 4900 || code === 4901)
    return "MetaMask non riesce a raggiungere la rete. Controlla la connessione e l’RPC configurato.";
  return `${error?.message || "Errore nel collegamento a MetaMask"}${error?.method ? ` [${error.method}, codice ${rpcErrorCode(error) ?? "sconosciuto"}]` : ""}`;
}

export async function walletRequest(wallet, method, params) {
  const identity = { name: wallet.info?.name, rdns: wallet.info?.rdns };
  // eslint-disable-next-line no-console
  console.debug("Wallet RPC", { method, provider: identity });
  try {
    return await wallet.provider.request({
      method,
      ...(params ? { params } : {}),
    });
  } catch (cause) {
    const error = new Error(cause?.message || "Errore interno MetaMask");
    error.code = rpcErrorCode(cause);
    error.method = method;
    error.cause = cause;
    // eslint-disable-next-line no-console
    console.error("Wallet RPC failed", {
      method,
      provider: identity,
      code: error.code,
      message: error.message,
      cause,
    });
    throw error;
  }
}

export async function requireAmoy(wallet, switchNetwork = false) {
  let chainId = Number(await walletRequest(wallet, "eth_chainId"));
  if (chainId !== AMOY_CHAIN_ID && switchNetwork) {
    try {
      await walletRequest(wallet, "wallet_switchEthereumChain", [
        { chainId: AMOY_NETWORK.chainId },
      ]);
    } catch (error) {
      if (Number(rpcErrorCode(error)) !== 4902) throw error;
      await walletRequest(wallet, "wallet_addEthereumChain", [AMOY_NETWORK]);
      // Adding a chain does not guarantee it becomes the active chain.
      if (
        Number(await walletRequest(wallet, "eth_chainId")) !== AMOY_CHAIN_ID
      ) {
        await walletRequest(wallet, "wallet_switchEthereumChain", [
          { chainId: AMOY_NETWORK.chainId },
        ]);
      }
    }
    chainId = Number(await walletRequest(wallet, "eth_chainId"));
  }
  if (chainId !== AMOY_CHAIN_ID) {
    throw new Error(
      "Seleziona Polygon Amoy in MetaMask oppure premi Connect Wallet per cambiare rete.",
    );
  }
  return chainId;
}

// Construct ethers only after verifying the wallet's current network.
export async function getWalletBrowserProvider() {
  const wallet = await getMetaMaskWallet();
  await requireAmoy(wallet);
  return new BrowserProvider(wallet.provider);
}
